import React, { useEffect, useState, useContext } from "react";
import Screens from "../../model/Screens";
import { allowAdd, guidGenerator } from "../utils/commonFunctions";
import { replacer } from "../../model/Action";
import Popup from "reactjs-popup";
import { MachineContext } from "../fsm/useMachine";
import { REORDER_SCREENS } from "../fsm/types";
import { FlowWindow } from "../../globals";
import { Upgrade } from "../marketing/Upgrade";
import { ViewMenu } from "../control_bar/ViewMenu";
import log from "loglevel";
import { XCircle } from "react-feather";
import { FlowVellaLoader } from "../../components/FlowVellaLoader";

declare let window: FlowWindow;

// Module-level flag for Safari workaround (useRef doesn't work in Safari either!)
let templateApiCalled = false;

export const NewScreen: React.FC<{ theme_id?: boolean }> = ({ theme_id }) => {
  const [templates, setTemplates] = useState();
  const [shouldOverrideTemplates, setShouldOverride] = React.useState(false);

  // SAFARI FIX: useEffect doesn't fire in Safari, so call API directly in render
  if (!templateApiCalled && !theme_id && typeof window.$ !== 'undefined' && window.$.treemo) {
    templateApiCalled = true;

    // Safari fix: Delay the API call to next event loop tick
    setTimeout(() => {
      window.$.treemo.api.sendRequest({
        args: {
          method: "flowboard.getTemplates",
        },
        onSuccess: function (data: any) {
          setTemplates(data["templates"]);
        },
        onError: function (data: any) {
          log.error("Failed to load templates", data);
        },
      });
    }, 100);
  }

  if (window.newFlow) {
    return (
      <Templates
        templates={templates}
        overrideSettings={[shouldOverrideTemplates, setShouldOverride]}
      />
    );
  }

  const [current] = useContext(MachineContext);

  if (allowAdd(window.paidUser, current.context.flow.screens.length)) {
    return (
      <Popup
        trigger={
          <button className="screen_item new_screen" title="Add a New Screen">
            <div className="add_div">
              <label>
                <img
                  className="add height_80"
                  src="/include/img/creator/add-screen-icon.png"
                  style={{borderRadius: "20px"}}
                />
              </label>
            </div>
          </button>
        }
        className="bubble"
        position="top center"
        on="click"
      >
        {(close: () => void) => (
          <React.Fragment>
            <span className="close" onClick={close}>
              <XCircle className="feather" />
            </span>
            <ScreenOptions close_action={close} />
          </React.Fragment>
        )}
      </Popup>
    );
  } else {
    return (
      <UpgradeModal />
    );
  }
};

const UpgradeModal = () => (
  <Popup
    trigger={
      <button className="screen_item new_screen" title="Add a New Screen">
        <div className="add_div">
          <label>
            <img
              className="add height_80"
              src="/include/img/creator/add-screen-icon.png"
            />
          </label>
        </div>
      </button>
    }
    modal={true}
    closeOnDocumentClick={false}
    closeOnEscape={false}
  >
    {(close) => <ViewMenu close={close} render={(setArgs, setShowModal) => (<Upgrade close={close} setArgs={setArgs} setShowModal={setShowModal} />)} />}
  </Popup>
)

const ScreenOptions: React.FC<{ close_action: () => void }> = ({
  close_action,
}) => {
  // Check if we have template_flow_json as a fallback
  if (!window.template_flow_json?.flowboard?.flows?.[0]?.screens) {
    return <div>Error: Template data not loaded</div>;
  }

  // Try to get screen previews from published metadata
  let meta: any[] | null = null;

  if (
    window.template_flow_meta?.flowboard?.document_json_published_metadata &&
    typeof window.template_flow_meta.flowboard.document_json_published_metadata === 'object' &&
    window.template_flow_meta.flowboard.document_json_published_metadata.screen_previews
  ) {
    const previews = window.template_flow_meta.flowboard.document_json_published_metadata.screen_previews;

    meta = Object.keys(previews).map(function (key) {
      previews[key].id = key;
      return previews[key];
    });
  } else {
    // Fallback: use template screens directly from template_flow_json
    const templateScreens = window.template_flow_json.flowboard.flows[0].screens;

    // Create preview objects from template screens
    meta = templateScreens.map((screen: any) => ({
      id: screen.id,
      https: screen.preview_image || '/include/img/creator/blank-screen-preview.png', // Fallback image
    }));
  }

  if (!meta || meta.length === 0) {
    return <div>Error: No template screens available</div>;
  }

  return (
    <ul>
      {meta.map((screen: any) => {
        return (
          <li key={screen.id}>
            <AddScreen screen_id={screen.id} close_action={close_action}>
              <img src={screen.https} />
            </AddScreen>
          </li>
        );
      })}
    </ul>
  );
};

const Templates: React.FC<{
  templates: any;
  overrideSettings: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
}> = ({ templates, overrideSettings }) => {
  const [shouldOverrideTemplates, setShouldOverride] = overrideSettings;

  let templateBlankStandard: {
    string_id: string;
    id: string;
    themeIconImage: { icon: { url: string } };
  } = {
    string_id: "rqc",
    id: "210",
    themeIconImage: {
      icon: {
          url: "https://media.goboard.io/mac-standard.png",
      },
    },
  };

  let templateBlankWide: {
    string_id: string;
    id: string;
    themeIconImage: { icon: { url: string } };
  } = {
    string_id: "3zuu",
    id: "220",
    themeIconImage: {
      icon: {
        url: "https://media.goboard.io/mac-wide.png",
      },
    },
  };

  if (!templates) {
    return <FlowVellaLoader message="Loading Templates" />;
  }

  if (templates) {
    templates.unshift(templateBlankWide)
    templates.unshift(templateBlankStandard)
  }

  return templates && !shouldOverrideTemplates ? (
    <>
      <span
        className="close"
        onClick={() => window.location.href = '/user/myflows'}
        title="Close Creator"
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          cursor: 'pointer'
        }}
      >
        <XCircle className="feather" />
      </span>
      <ul style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '30px',
        padding: '20px',
        margin: 0,
        width: '100%',
        listStyle: 'none',
        overflowY: 'auto',
        flex: 1
      }}>
      {templates
        .filter((template: any) => {
          // Filter out templates with missing or invalid icon URLs
          try {
            if (!template.themeIconImage?.icon?.url) {
              log.warn("Template missing icon URL:", JSON.stringify(template));
              return false;
            }
            // Validate it's a proper URL
            new URL(template.themeIconImage.icon.url);
            return true;
          } catch (error) {
            log.warn(
              "Could not convert to URL:",
              template.themeIconImage?.icon?.url,
              JSON.stringify(template)
            );
            return false;
          }
        })
        .map((template: any) => {
          // Use the URL directly from the API (R2 URLs from database)
          const iconUrl = template.themeIconImage.icon.url;

          return (
            <li key={template.id}>
              <img
                src={iconUrl}
                onClick={() => {
                  setShouldOverride(true);
                  window.location.replace(
                    `/flow/${template.string_id}/template/${template.id}`
                  );
                }}
              />
            </li>
          );
        })}
    </ul>
    </>
  ) : (
      <FlowVellaLoader message="Loading the Template and Creating your new Flow" />
    );
};

const AddScreen: React.FC<{
  children: React.ReactNode;
  screen_id: string;
  close_action: () => void;
}> = ({ children, screen_id, close_action }) => {
  const [current, send] = useContext(MachineContext);
  return (
    <a
      onClick={() => {
        const { flowboard } = window.template_flow_json;
        const { flow, index } = current.context;

        // Find the template screen
        const templateJSON = flowboard.flows[0].screens.find(
          (screen: Screens) => screen.id == screen_id
        );

        // Create a screen from JSON (this creates new MMO objects)
        const template_screen = Screens.fromJSON(templateJSON);

        // Clone it to ensure complete isolation from template
        const new_screen = template_screen.clone();

        // Assign new IDs to screen and all MMOs
        new_screen.id = guidGenerator();
        new_screen.mixedMediaObjects.forEach((mmo) => {
          mmo.id = guidGenerator();
          mmo.__ref = undefined; // Ensure no refs from template
        });

        // Create new array and insert the screen
        const screens = [...flow.screens];
        screens.splice(index + 1, 0, new_screen);
        console.info(screens, new_screen, "ADD");
        send({ type: REORDER_SCREENS, screens, newIndex: index + 1 });
        close_action();
      }}
    >
      {children}
    </a>
  );
};

export default NewScreen;
